import { Injectable } from '@angular/core';
import { OperatorNamesList } from '../api/models/operator-names-list';
import { map } from 'lodash';
import { AssigneeTypes } from '../common/constants/boldchat-enums';
import { ActiveWorkItemCountModel } from '../api/models/active-work-item-count.model';
import { WorkItemTypes } from '../workitems/constants/workitem-types';
import { BcRestApi } from 'common/api/rest-api';

@Injectable()
export class OperatorsService {

  constructor(private restApi: BcRestApi) {
  }

  getOperatorsWithAvailability() {
    return this.restApi.getOperatorsWithAvailability();
  }

  getOperatorsNamesByDepartmentPermission(): Promise<OperatorNamesList[]> {
    return this.restApi.getOperatorsNamesByDepartmentPermission();
  }

  getDepartmentsWithOperators() {
    return this.restApi.getDepartmentsWithOperators();
  }

  getOperatorsAndDepartmentsWithAvailability() {
    const operators = this.getOperatorsWithAvailability();
    const departments = this.getDepartmentsWithOperators();
    return Promise.all([departments, operators])
      .then((values) => {
        const nonEmptyDepartments = values[0].filter((department) => department.operators.length > 0);
        return this.mergeDepartmentsWithOperators(nonEmptyDepartments, values[1]);
      });
  }

  public assignChat(chatId: string, assigneeId: string, assigneeType: AssigneeTypes): Promise<void> {
    return this.restApi.assignChat(chatId, assigneeId, assigneeType);
  }

  public assignChats(chatIds: string[], assigneeId: string, assigneeType: AssigneeTypes): Promise<void> {
    return this.restApi.assignChats(chatIds, assigneeId, assigneeType);
  }

  public assignEmail(emailId: string, assigneeId: string, assigneeType: AssigneeTypes): Promise<void> {
    return this.restApi.assignEmail(emailId, assigneeId, assigneeType);
  }

  public assignEmails(emailIds: string[], assigneeId: string, assigneeType: AssigneeTypes): Promise<void> {
    return this.restApi.assignEmails(emailIds, assigneeId, assigneeType);
  }

  public assignTextChat(textChatId: string, assigneeId: string, assignType: AssigneeTypes): Promise<void> {
    return this.restApi.assignTextChat(textChatId, assigneeId, assignType);
  }

  public assignTextChats(textChatIds: string[], assigneeId: string, assigneeType: AssigneeTypes): Promise<void> {
    return this.restApi.assignTextChats(textChatIds, assigneeId, assigneeType);
  }

  public assignWorkItemFromDiscussion(workItemId: string, workItemType: WorkItemTypes, assigneeId: string, assigneeType: AssigneeTypes, assigneeDepartmentId: string): Promise<void> {
    switch (workItemType) {
      case WorkItemTypes.CHAT:
        if (assigneeDepartmentId === '0') {
          assigneeDepartmentId = null;
        }
        return this.restApi.assignChatFromDiscussion(workItemId, assigneeId, assigneeType, assigneeDepartmentId);
      case WorkItemTypes.EMAIL:
        return this.assignEmail(workItemId, assigneeId, assigneeType);
      default:
        throw new Error('Unhandled workItem type in assign from discussion');
    }
  }

  escalateChats(chatIds: string[], assigneeId: string, assigneeType: AssigneeTypes): Promise<void> {
    return this.restApi.escalateChats(chatIds, assigneeId, assigneeType);
  }

  mergeDepartmentsWithOperators(departments, operators) {
    departments.forEach((department) => {
      if (department.operatorsVisible) {
        const departmentOperatorIds = map(department.operators, (op: any) => op.id);
        department.operators = this.findOperatorsForDepartment(operators, departmentOperatorIds);
      } else {
        department.operators = [];
      }
    });
    return departments;
  }

  findOperatorsForDepartment(operators, departmentOperatorIds) {
    const actualOperatorList = [];
    operators.forEach((currentOperator) => {
      if (departmentOperatorIds.includes(currentOperator.id)) {
        actualOperatorList.push(currentOperator);
      }
    });
    return actualOperatorList;
  }

  getActiveChatCounts(): Promise<ActiveWorkItemCountModel[]> {
    return this.restApi.getActiveChatCounts();
  }

  getActiveTextChatCounts(): Promise<ActiveWorkItemCountModel[]> {
    return this.restApi.getActiveTextChatCounts();
  }
}
